import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical'
import styles from '../EmailEditor.module.scss'
import { requireName, requireString } from './serializedKeys'

export type SerializedVariableNode = Spread<{ variable: string }, SerializedTextNode>

type ChipState = 'valid' | 'invalid'

const CHIP_BASE_CLASSES = [styles.chip, 'zocks-px-0.25', 'rounded-1']

const CHIP_STATE_CLASSES: Record<ChipState, string[]> = {
  valid: ['zocks-bg-color-primary-light', 'zocks-text-primary-dark'],
  invalid: ['zocks-bg-color-danger-background', 'zocks-text-danger-200', 'text-decoration-underline', styles.chipInvalid],
}

export const CHIP_CLASSES = [...CHIP_BASE_CLASSES, ...CHIP_STATE_CLASSES.valid]

const applyChipClasses = (dom: HTMLElement, state: ChipState) => {
  dom.classList.remove(...CHIP_STATE_CLASSES.valid, ...CHIP_STATE_CLASSES.invalid)
  dom.classList.add(...CHIP_BASE_CLASSES, ...CHIP_STATE_CLASSES[state])
}

export class VariableNode extends TextNode {
  __variable: string
  __invalid = false

  static getType(): string {
    return 'variable'
  }

  static clone(node: VariableNode): VariableNode {
    const copy = new VariableNode(node.__variable, node.__text, node.__key)
    copy.__invalid = node.__invalid
    return copy
  }

  constructor(variable: string, label: string, key?: NodeKey) {
    super(label, key)
    this.__variable = variable
  }

  static importJSON(json: SerializedVariableNode): VariableNode {
    const raw = json as unknown as Record<string, unknown>
    return $createVariableNode(requireName(raw, 'variable', 'variable'), requireString(raw, 'variable', 'text')).updateFromJSON(json)
  }

  static importDOM(): DOMConversionMap | null {
    return null
  }

  exportJSON(): SerializedVariableNode {
    return { ...super.exportJSON(), variable: this.__variable }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    applyChipClasses(dom, this.__invalid ? 'invalid' : 'valid')
    dom.dataset.variable = this.__variable
    dom.title = `{{ ${this.__variable} }}`
    return dom
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const changed = super.updateDOM(prevNode, dom, config)
    applyChipClasses(dom, this.__invalid ? 'invalid' : 'valid')
    return changed
  }

  exportDOM(): DOMExportOutput {
    const span = document.createElement('span')
    span.textContent = `{{ ${this.__variable} }}`
    return { element: span }
  }

  isInvalid(): boolean {
    return this.getLatest().__invalid
  }

  setInvalid(invalid: boolean): this {
    const writable = this.getWritable()
    writable.__invalid = invalid
    return writable
  }

  getVariable(): string {
    return this.getLatest().__variable
  }

  isTextEntity(): true {
    return true
  }

  canInsertTextBefore(): boolean {
    return false
  }

  canInsertTextAfter(): boolean {
    return false
  }
}

export const $createVariableNode = (variable: string, label: string): VariableNode => {
  const node = new VariableNode(variable, label)
  node.setMode('token').toggleDirectionless()
  return $applyNodeReplacement(node)
}

export const $isVariableNode = (node: LexicalNode | null | undefined): node is VariableNode => node instanceof VariableNode
